import { ACKNOWLEDGE_SR_ERROR_MESSAGES } from '../errors/errorMessages';

/**
 * UI strings for the service type management module.
 * Covers list, create, edit, view pages and the status-toggle confirmation dialog.
 * Used by `ServiceTypeList`, `CreateServiceType`, `EditServiceType`, `ViewServiceType`.
 */
export const SERVICE_TYPE_LABELS = {
  // Page titles
  LIST_TITLE: 'Typy usług',
  CREATE_TITLE: 'Dodaj typ usługi',
  VIEW_TITLE: 'Podgląd typu usługi',
  EDIT_TITLE: 'Edytuj typ usługi',

  // Section headings
  SECTION_DETAILS: 'Szczegóły typu usługi',
  SECTION_BASIC_INFO: 'Podstawowe informacje',

  // Field labels
  FIELD_NAME: 'Nazwa',
  FIELD_DESCRIPTION: 'Opis',
  FIELD_CREATED_AT: 'Data utworzenia',
  FIELD_UPDATED_AT: 'Data modyfikacji',
  FIELD_UPDATED_BY: 'Zmodyfikowane przez',
  FIELD_STATUS: 'Status:',

  // Buttons
  BUTTON_CREATE: 'Dodaj typ usługi',
  BUTTON_CREATE_LOADING: 'Dodawanie typu usługi…',
  BUTTON_CANCEL: 'Anuluj',
  BUTTON_EDIT: 'Edytuj typ usługi',
  BUTTON_SAVE: 'Zapisz',
  BUTTON_SAVE_LOADING: 'Zapisywanie…',

  // View error screen
  VIEW_ERROR_TITLE: 'Coś poszło nie tak...',
  VIEW_ERROR_DESCRIPTION:
    'Nie udało się w tej chwili załadować szczegółów typu usługi.',
  VIEW_ERROR_RELOAD: 'Odśwież',

  // Success
  CREATE_SUCCESS: 'Typ usługi został pomyślnie dodany.',
  EDIT_SUCCESS: 'Dane typu usługi zostały pomyślnie zaktualizowane.',

  // Empty state
  EMPTY_STATE_TITLE: 'Brak typów usług.',
  EMPTY_STATE_DESCRIPTION:
    'Typy usług pojawią się tutaj po dodaniu ich do systemu.',

  // No search / filter results
  NO_RESULTS_TITLE: 'Nie znaleziono wyników.',
  NO_RESULTS_DESCRIPTION_SEARCH: 'Spróbuj zmienić kryteria wyszukiwania.',
  NO_RESULTS_DESCRIPTION_FILTER: 'Spróbuj zmienić ustawienia filtrów.',
  NO_RESULTS_DESCRIPTION_BOTH:
    'Spróbuj zmienić kryteria wyszukiwania lub filtry.',

  // List table / controls
  SEARCH_PLACEHOLDER: 'Szukaj po nazwie',
  COLUMN_NAME: 'Nazwa',
  COLUMN_DESCRIPTION: 'Opis',
  COLUMN_CREATED_AT: 'Data utworzenia',
  COLUMN_STATUS: 'Status',
  COLUMN_ACTIONS: 'Akcje',
  STATUS_ACTIVE: 'Aktywny',
  STATUS_INACTIVE: 'Nieaktywny',

  // Status confirmation dialog
  STATUS_DIALOG_ACTIVATE_TITLE: 'Aktywować typ usługi?',
  STATUS_DIALOG_DEACTIVATE_TITLE: 'Dezaktywować typ usługi?',
  STATUS_DIALOG_ACTIVATE_PREFIX: 'Czy na pewno chcesz aktywować typ usługi ',
  STATUS_DIALOG_ACTIVATE_SUFFIX: '?',
  STATUS_DIALOG_DEACTIVATE_PREFIX:
    'Czy na pewno chcesz dezaktywować typ usługi ',
  STATUS_DIALOG_DEACTIVATE_SUFFIX: '?',
  STATUS_DIALOG_CONFIRM_ACTIVATE: 'Aktywuj',
  STATUS_DIALOG_CONFIRM_DEACTIVATE: 'Dezaktywuj',
  STATUS_DIALOG_CONFIRM_ACTIVATING: 'Aktywowanie…',
  STATUS_DIALOG_CONFIRM_DEACTIVATING: 'Dezaktywowanie…',
  STATUS_SUCCESS_ACTIVATED: 'Typ usługi został pomyślnie aktywowany.',
  STATUS_SUCCESS_DEACTIVATED: 'Typ usługi został pomyślnie dezaktywowany.',
} as const;

/**
 * UI strings for the multi-step "raise service request" flow.
 * Covers step labels, facility/equipment/description/media fields, the step-3 preview,
 * and submission buttons.
 * Used by `CreateServiceRequest`, `Step1FacilityEquipment`, `Step2DetailsAttachments`, `Step3Preview`.
 */
export const RAISE_SERVICE_REQUEST_LABELS = {
  PAGE_TITLE: 'Zamów usługę',

  // Stepper labels
  STEP1_LABEL: 'Szczegóły zgłoszenia',
  STEP2_LABEL: 'Dodaj szczegóły i załączniki',
  STEP3_LABEL: 'Przegląd i wysłanie',

  // Section: Selected service
  SECTION_SERVICE: 'Wybrana usługa',
  SERVICE_PRICE_PLACEHOLDER: '-',

  // Section: Facility
  SECTION_FACILITY: 'Placówka',
  FACILITY_DESCRIPTION: 'Wybierz placówkę, w której wymagana jest usługa.',
  FIELD_FACILITY: 'Placówka',
  FACILITY_REQUIRED_ERROR: 'To pole jest wymagane',
  FACILITY_DISABLED_TOOLTIP: 'Brak uprawnień do wyświetlenia placówek.',
  FACILITY_LOADING: 'Ładowanie…',
  FACILITY_NO_RESULTS: 'Brak dostępnych placówek',

  // Section: Equipment
  SECTION_EQUIPMENT: 'Aparatura',
  EQUIPMENT_DESCRIPTION:
    'Wybierz jedną lub więcej pozycji aparatury wymagających serwisu.',
  FIELD_EQUIPMENT: 'Aparatura',
  FIELD_EQUIPMENT_OPTIONAL: '(Opcjonalne)',
  EQUIPMENT_SELECT_FACILITY_FIRST:
    'Please select a facility to view available equipment',
  EQUIPMENT_DISABLED_TOOLTIP: 'Brak uprawnień do wyświetlenia urządzeń.',
  EQUIPMENT_LOADING: 'Ładowanie…',
  EQUIPMENT_NO_RESULTS: 'Brak urządzeń dla tej placówki',

  // Section: Description
  SECTION_DESCRIPTION: 'Opis',
  DESCRIPTION_HELPER:
    'Podaj dodatkowe informacje, które pomogą dokładnie opisać problem.',
  FIELD_DESCRIPTION_PLACEHOLDER: 'Opis',
  DESCRIPTION_REQUIRED_ERROR: 'To pole jest wymagane',
  DESCRIPTION_MAX_LENGTH: 500,

  // Section: Media and documents
  SECTION_MEDIA: 'Media i dokumenty',
  SECTION_IMAGES: 'Zdjęcia',
  IMAGES_DESCRIPTION: 'Możesz przesłać maksymalnie 2 pliki.',
  IMAGES_REQUIRED_ERROR: 'To pole jest wymagane',
  SECTION_DOCUMENT: 'Dokument',
  DOCUMENT_DESCRIPTION: 'Możesz przesłać maksymalnie 1 plik.',
  DOCUMENT_REQUIRED_ERROR: 'To pole jest wymagane',

  // Step 3: Preview
  STEP3_SECTION_TITLE: 'Przegląd zgłoszenia',
  STEP3_SECTION_SUBTITLE: 'Sprawdź szczegóły przed wysłaniem zgłoszenia.',
  SECTION_USER_DATA: 'Dane użytkownika',
  SECTION_FACILITY_DATA: 'Dane placówki',
  SECTION_ADDRESS_DATA: 'Dane adresowe',
  SECTION_EQUIPMENT_DATA: 'Dane aparatury',
  SECTION_DETAILS: 'Szczegóły i załączniki',

  // Preview field labels
  LABEL_CLIENT_NAME: 'Nazwa',
  LABEL_CLIENT_ID: 'ID klienta',
  LABEL_ROLE: 'Rola',
  LABEL_ROLE_VALUE: 'Klient',
  LABEL_FACILITY_NAME: 'Placówka',
  LABEL_FACILITY_ID: 'ID placówki',
  LABEL_ADDRESS1: 'Adres (linia 1)',
  LABEL_ADDRESS2: 'Adres (linia 2)',
  LABEL_CITY: 'Miejscowość',
  LABEL_STATE: 'Województwo',
  LABEL_ZIP: 'Kod pocztowy',
  LABEL_COUNTRY: 'Kraj',
  LABEL_EQUIP_TABLE_NAME: 'Nazwa',
  LABEL_EQUIP_TABLE_ID: 'ID aparatury',
  LABEL_DESCRIPTION: 'Opis',
  LABEL_IMAGES: 'Zdjęcia',
  LABEL_DOCUMENT: 'Dokument',
  LABEL_EMPTY: '-',

  // Buttons
  BUTTON_CANCEL: 'Anuluj',
  BUTTON_BACK: 'Wstecz',
  BUTTON_NEXT: 'Dalej',
  BUTTON_SUBMIT: 'Wyślij',
  BUTTON_SUBMITTING: 'Wysyłanie…',

  // Error messages
  SUBMIT_ERROR:
    'Nie udało się złożyć zgłoszenia serwisowego. Spróbuj ponownie.',
  UPLOAD_ERROR: 'Nie udało się przesłać pliku. Spróbuj ponownie.',
  SERVICE_TYPE_LOAD_ERROR: 'Nie udało się załadować danych usługi.',

  // Privilege-related
  NO_PRIVILEGE_TOOLTIP: 'Brak uprawnień do złożenia zgłoszenia serwisowego.',
} as const;

/**
 * UI strings for the "Produkty i usługi" page.
 * Covers the page title, tab labels, and empty-state copy.
 * Used by `AvailableServiceList`.
 */
export const AVAILABLE_SERVICES_LABELS = {
  // Page title
  PAGE_TITLE: 'Produkty i usługi',

  // Tabs
  TAB_AVAILABLE: 'Dostępne usługi',
  TAB_MY_REQUESTS: 'Moje zgłoszenia',

  // Buttons
  BUTTON_REQUEST: 'Zamów usługę',

  // Empty state
  EMPTY_NO_SERVICES_TITLE: 'Brak dostępnych usług.',
  EMPTY_NO_SERVICES:
    'Sprawdź ponownie później lub skontaktuj się z administratorem, aby uzyskać więcej informacji.',

  // My Requests placeholder
  MY_REQUESTS_COMING_SOON: 'Moje zgłoszenia – wkrótce dostępne.',
} as const;

/**
 * UI strings for the service request list table (both client "Moje zgłoszenia" and admin views).
 * Covers column headers, search/filter controls, status chip labels, action menu, and empty states.
 * Used by `MyRequestsTab`, `AdminServiceRequestsTab`, `useMyRequestsList`, `useAdminServiceRequestsList`.
 */
export const MY_REQUESTS_LABELS = {
  // Search
  SEARCH_PLACEHOLDER: 'Szukaj po ID zgłoszenia',
  ADMIN_SEARCH_PLACEHOLDER:
    'Szukaj po ID zgłoszenia, nazwie klienta lub nazwie placówki',

  // Toolbar
  FILTER_BUTTON: 'Filtruj',
  COLUMNS_BUTTON: 'Columns',

  // Columns (Polish)
  COLUMN_REQUEST_ID: 'ID zgłoszenia',
  COLUMN_EQUIPMENT_NAME: 'Nazwa aparatury',
  COLUMN_EQUIPMENT_ID: 'ID aparatury',
  COLUMN_SERVICE_TYPE: 'Typ usługi',
  COLUMN_SUBMITTED_ON: 'Data zgłoszenia',
  COLUMN_FACILITY_NAME: 'Nazwa placówki',
  COLUMN_FACILITY_ID: 'ID placówki',
  COLUMN_ASSIGNED_TO: 'Przydzielono do',
  COLUMN_PHONE_NUMBER: 'Numer telefonu',
  COLUMN_STATUS: 'Status',
  COLUMN_ACTIONS: 'Akcje',
  EMPTY_CELL: '-',

  // Status chip labels (Polish)
  STATUS_PENDING: 'Oczekuje',
  STATUS_IN_REVIEW: 'W trakcie weryfikacji',
  STATUS_ASSIGNED: 'Przydzielone',
  STATUS_ACCEPTED: 'Zaakceptowane',
  STATUS_REJECTED: 'Odrzucone',
  STATUS_IN_PROGRESS: 'W realizacji',
  STATUS_DELAYED: 'Opóźnione',
  STATUS_COMPLETED: 'Zakończone',
  STATUS_CLOSED: 'Zamknięte',

  // Admin-only columns
  COLUMN_CLIENT_NAME: 'Nazwa klienta',
  COLUMN_CLIENT_ID: 'ID klienta',

  // Admin empty state
  EMPTY_ADMIN_TITLE: 'Brak zgłoszeń serwisowych.',
  EMPTY_ADMIN_DESCRIPTION:
    'Zgłoszenia serwisowe pojawią się tutaj po ich dodaniu.',

  // Action menu
  ACTION_VIEW: 'Podgląd',
  ACTION_VIEW_ARIA: 'Podgląd zgłoszenia',

  // Empty / no results
  EMPTY_TITLE: 'Brak zgłoszeń serwisowych.',
  EMPTY_DESCRIPTION:
    'Tutaj będą wyświetlane Twoje zgłoszenia serwisowe dotyczące aparatury.',
  EMPTY_BUTTON: 'Złóż zgłoszenie',
  NO_RESULTS_TITLE: 'Nie znaleziono wyników.',
  NO_RESULTS_DESCRIPTION_SEARCH: 'Spróbuj zmienić kryteria wyszukiwania.',
  NO_RESULTS_DESCRIPTION_FILTER: 'Spróbuj zmienić ustawienia filtrów.',
  NO_RESULTS_DESCRIPTION_BOTH:
    'Spróbuj zmienić kryteria wyszukiwania lub filtry.',
} as const;

/**
 * UI strings for the view service request detail page.
 * Covers the page title, side-nav tab labels, section headings, field labels,
 * role display values, history timeline copy, view error screen, and the
 * cancellation window banners shown to CLIENT users.
 * Used by `ViewServiceRequest` and all `SR*Tab` sub-components.
 */
export const VIEW_SERVICE_REQUEST_LABELS = {
  PAGE_TITLE: 'Podgląd zgłoszenia serwisowego',
  BUTTON_CANCEL_REQUEST: 'Anuluj zgłoszenie',
  BUTTON_CHANGE_STATUS: 'Zmiana statusu',
  BUTTON_ASSIGN: 'Przydziel',
  BUTTON_RESCHEDULE: 'Zmień termin',
  // Technician actions (View details — tablet, shown when status is "assigned")
  BUTTON_ACCEPT: 'Zaakceptuj',
  BUTTON_REJECT: 'Odrzuć',
  BUTTON_RESCHEDULE_TOOLTIP_24H:
    'Tego zgłoszenia nie można już anulować, ponieważ do zaplanowanej wizyty pozostało mniej niż 24 godziny.',
  BANNER_CANNOT_CANCEL_WITHIN_24H:
    'Tego zgłoszenia nie można już anulować, ponieważ do zaplanowanej wizyty pozostało mniej niż 24 godziny.',

  // Side nav tabs
  NAV_SUMMARY: 'Podsumowanie zgłoszenia',
  NAV_FACILITY: 'Dane placówki',
  NAV_EQUIPMENT: 'Dane aparatury',
  NAV_DETAILS: 'Szczegóły i załączniki',
  NAV_TECHNICIAN: 'Dane technika',
  NAV_ADMIN_NOTES: 'Notatki administratora',
  NAV_HISTORY: 'Historia',

  // Section headings
  SECTION_SELECTED_SERVICE: 'Wybrana usługa',
  SECTION_USER_DATA: 'Dane użytkownika',
  SECTION_FACILITY_DATA: 'Dane placówki',
  SECTION_ADDRESS_DATA: 'Dane adresowe',
  SECTION_EQUIPMENT_DATA: 'Dane aparatury',
  SECTION_DESCRIPTION: 'Opis',
  SECTION_IMAGES: 'Zdjęcia',
  SECTION_DOCUMENT: 'Dokument',
  SECTION_TECHNICIAN_DATA: 'Dane technika',
  SECTION_SCHEDULED_VISIT: 'Szczegóły zaplanowanej wizyty',

  // Field labels
  FIELD_PROFILE_PICTURE: 'Zdjęcie profilowe',
  FIELD_CLIENT_NAME: 'Nazwa',
  FIELD_CLIENT_ID: 'ID klienta',
  FIELD_ROLE: 'Rola',
  FIELD_SUBMITTED_ON: 'Data zgłoszenia',
  FIELD_FACILITY_NAME: 'Placówka',
  FIELD_FACILITY_ID: 'ID placówki',
  FIELD_ADDRESS1: 'Adres (linia 1)',
  FIELD_ADDRESS2: 'Adres (linia 2)',
  FIELD_CITY: 'Miejscowość',
  FIELD_STATE: 'Województwo',
  FIELD_ZIP: 'Kod pocztowy',
  FIELD_COUNTRY: 'Kraj',
  FIELD_EQUIPMENT_NAME: 'Nazwa',
  FIELD_EQUIPMENT_ID: 'ID aparatury',
  FIELD_TECHNICIAN_NAME: 'Nazwa',
  FIELD_TECHNICIAN_ID: 'ID technika',
  FIELD_TECHNICIAN_EMAIL: 'Adres e-mail',
  FIELD_TECHNICIAN_PHONE: 'Numer telefonu',
  FIELD_SCHEDULED_DATE: 'Data',
  FIELD_SCHEDULED_TIME: 'Godzina',
  FIELD_ASSIGNED_BY: 'Assigned By',

  // Role display values
  ROLE_CLIENT: 'Klient',
  ROLE_ADMIN: 'Administrator',
  ROLE_SUPER_ADMIN: 'Super Admin',
  ROLE_TECHNICIAN: 'Technik',

  // Placeholders
  EMPTY_CELL: '-',
  PRICE_PLACEHOLDER: '-',

  // Empty-state copy for reserved tabs
  EMPTY_TECHNICIAN_MESSAGE: 'Technik nie został jeszcze przydzielony.',
  EMPTY_ADMIN_NOTES_MESSAGE: 'Administrator nie dodał jeszcze żadnych notatek.',

  // History tab
  HISTORY_STATUS_CHANGE: 'Zmiana statusu:', // Status changed:
  HISTORY_TECHNICIAN_ASSIGNED: 'Technik przydzielony', // Technician assigned
  HISTORY_TECHNICIAN_ACCEPTED: 'Technik zaakceptował zadanie', // Technician accepted the task
  HISTORY_TECHNICIAN_REJECTED: 'Technik odrzucił zadanie', // Technician rejected the task
  HISTORY_SERVICE_REQUEST_CREATED: 'Zgłoszenie serwisowe utworzone', // Service request created
  HISTORY_UPDATED_BY: 'Zaktualizowane przez', // Updated by
  HISTORY_CREATED_BY: 'Utworzono przez', // Created by
  HISTORY_EMPTY_TITLE: 'Brak historii.', // No history.
  HISTORY_EMPTY_DESCRIPTION:
    'Zmiany statusu i działania będą pojawiać się tutaj w miarę realizacji zgłoszenia.',
  // Status changes and actions will appear here as your request progresses.

  // Empty inline states
  EMPTY_EQUIPMENT_MESSAGE: 'No equipments are added yet.',

  // View error screen
  VIEW_ERROR_TITLE: 'Coś poszło nie tak...',
  VIEW_ERROR_DESCRIPTION:
    'Nie udało się w tej chwili załadować szczegółów zgłoszenia.',
  VIEW_ERROR_RELOAD: 'Odśwież',

  // Cancellation window banners (CLIENT only, shown when a visit is scheduled)
  /** Amber warning banner — visit is < 24 h away; cancel button is disabled. */
  BANNER_CANCEL_DISABLED:
    'Tego zgłoszenia nie można już anulować, ponieważ do zaplanowanej wizyty pozostało mniej niż 24 godziny.',
  /** Info banner — visit is ≥ 24 h away; cancel button is enabled. */
  BANNER_CANCEL_INFO:
    'Możesz anulować to zgłoszenie najpóźniej 24 godziny przed zaplanowaną wizytą.',
} as const;

/**
 * UI strings for the assign-technician modal.
 * Covers schedule window fields, technician picker, time dialog, buttons, and validation messages.
 * {@link ASSIGN_TECHNICIAN_LABELS.ERROR_REQUIRED} is the shared required-field message.
 * Used by `AssignTechnicianModal` and `useAssignTechnician`.
 */
export const ASSIGN_TECHNICIAN_LABELS = {
  // Modal
  TITLE: 'Przydziel zgłoszenie serwisowe',

  // Section headings
  SECTION_SCHEDULE: 'Szczegóły harmonogramu',
  SECTION_TECHNICIAN: 'Dane technika',

  // Window fields
  FIELD_START_DATE: 'Data początkowa',
  FIELD_END_DATE: 'Data końcowa',
  FIELD_START_TIME: 'Czas rozpoczęcia',
  FIELD_END_TIME: 'Czas zakończenia',
  FIELD_TIME_OPTIONAL: '(Opcjonalne)',

  // Technician picker
  FIELD_TECHNICIAN: 'Imię i nazwisko technika',
  FIELD_TECHNICIAN_ID: 'ID technika',
  SEARCH_PLACEHOLDER: 'Szukaj po imieniu lub nazwisku',
  TECHNICIAN_HELPER_NO_WINDOW:
    'Wybierz przedział czasu, aby zobaczyć dostępnych techników.',
  TECHNICIAN_TOOLTIP_NO_WINDOW:
    'Please select a start date and end date first.',
  TECHNICIAN_LOADING: 'Ładowanie…',
  TECHNICIAN_NO_RESULTS: 'Nie znaleziono wyników.',

  // Time picker dialog
  TIME_DIALOG_TITLE: 'Wprowadź godzinę',
  TIME_DIALOG_HOUR: 'Godzina',
  TIME_DIALOG_MINUTE: 'Minuta',
  TIME_DIALOG_OK: 'OK',

  // Buttons
  BUTTON_CANCEL: 'Anuluj',
  BUTTON_SUBMIT: 'Przydziel',
  BUTTON_SUBMITTING: 'Przydzielanie…',

  // Success snackbar
  SUCCESS_MESSAGE: 'Zgłoszenie serwisowe zostało pomyślnie przypisane.',

  // Task form picker
  FIELD_TASK_FORM: 'Wybierz formularz zadania',
  FIELD_TASK_FORM_ID: 'Task Form ID',
  TASK_FORM_LOADING: 'Ładowanie…',
  TASK_FORM_NO_RESULTS: 'Brak dostępnych formularzy.',

  // Certificate auto-populate
  FIELD_CERTIFICATE_TEMPLATE_NAME: 'Certificate Template Name',
  FIELD_CERTIFICATE_TEMPLATE_ID: 'Certificate Template ID',
  CERTIFICATE_LOADING: 'Ładowanie…',
  ERROR_NO_CERTIFICATE_LINKED: 'This task form does not have a certificate',
  CREATE_CERTIFICATE_LINK: 'Utwórz certyfikat',

  // Validation
  ERROR_REQUIRED: 'To pole jest wymagane',
  ERROR_START_DATE_AFTER_END:
    'Data początkowa nie może być późniejsza niż data końcowa.',
  ERROR_START_TIME_AFTER_END:
    'Czas rozpoczęcia nie może być późniejszy niż czas zakończenia.',
  ERROR_MIN_DURATION:
    'Minimalny czas trwania musi wynosić co najmniej 30 minut.',
} as const;

/**
 * UI strings for the update-service-request-status modal (admin only).
 * Covers field labels, the irreversibility banner, dropdown empty state,
 * buttons, success snackbar, and validation.
 *
 * @remarks
 * `NOTES_MAX_LENGTH` (500) is the shared character limit for admin notes fields
 * and is reused by {@link CANCEL_SERVICE_REQUEST_LABELS}.
 * `ERROR_REQUIRED` is the shared required-field message reused across status-update forms.
 *
 * Used by `UpdateStatusModal` and `useUpdateServiceRequestStatus`.
 */
export const UPDATE_SR_STATUS_LABELS = {
  // Modal
  TITLE: 'Zmiana statusu zgłoszenia serwisowego',

  // Field labels
  FIELD_CURRENT_STATUS: 'Aktualny status',
  FIELD_NEW_STATUS: 'Nowy status',
  FIELD_ADMIN_NOTES: 'Notatki administratora',
  FIELD_ADMIN_NOTES_OPTIONAL: '(Opcjonalne)',
  FIELD_NOTIFY: 'Send email',

  // Placeholders
  PLACEHOLDER_NEW_STATUS: 'Nowy status',
  PLACEHOLDER_ADMIN_NOTES: 'Notatki administratora',

  // Irreversibility banner
  BANNER_IRREVERSIBLE_BOLD: 'Zmiana statusu jest nieodwracalna.',
  BANNER_IRREVERSIBLE_REST:
    ' Po zaktualizowaniu nie będzie można przywrócić poprzedniego statusu.',

  // Helper / empty states
  HELPER_NO_TRANSITIONS: 'Status tego zgłoszenia nie może być już zmieniony.',
  EMPTY_NO_OPTIONS: 'Brak dostępnych statusów',

  // Buttons
  BUTTON_CANCEL: 'Anuluj',
  BUTTON_SUBMIT: 'Zmiana statusu',
  BUTTON_SUBMITTING: 'Aktualizowanie statusu…',
  BUTTON_CONTINUE: 'Continue',
  BUTTON_BACK: 'Wstecz',
  BUTTON_CONFIRM_CANCELLATION: 'Confirm Cancellation',

  // Success snackbar
  SUCCESS_MESSAGE: 'Status został pomyślnie zaktualizowany.',

  // Validation
  ERROR_REQUIRED: 'To pole jest wymagane',
  ERROR_NOTES_TOO_LONG: 'Notatka nie może przekraczać 500 znaków.',
  ERROR_REASON_TOO_LONG: 'Reason cannot exceed 500 characters.',
  NOTES_MAX_LENGTH: 500,

  // Cancel-reason step (only shown when newStatus = 'cancelled')
  CANCEL_STEP_SUBTITLE:
    'Cancelling this request will stop the scheduled visit, free the technician’s time slot, and notify all concerned parties.',
  CANCEL_STEP_VISIT_SECTION: 'Szczegóły zaplanowanej wizyty',
  CANCEL_STEP_START_DATE: 'Data początkowa',
  CANCEL_STEP_END_DATE: 'Data końcowa',
  CANCEL_STEP_START_TIME: 'Czas rozpoczęcia',
  CANCEL_STEP_END_TIME: 'Czas zakończenia',
  CANCEL_STEP_SERVICE_TYPE: 'Typ usługi',
  CANCEL_STEP_TECHNICIAN: 'Przydzielony użytkownik',
  CANCEL_STEP_CLIENT: 'Nazwa klienta',
  CANCEL_STEP_REASON_LABEL: 'Cancellation Reason',
  CANCEL_STEP_CUSTOM_REASON_LABEL: 'Reason',
  CANCEL_STEP_CUSTOM_REASON_PLACEHOLDER: 'Enter a Reason for Cancellation',
  CANCEL_REASON_CLIENT_UNAVAILABLE: 'Client unavailable',
  CANCEL_REASON_TECHNICIAN_UNAVAILABLE: 'Technician unavailable',
  CANCEL_REASON_DUPLICATE_BOOKING: 'Duplicate booking',
  CANCEL_REASON_INCORRECT_SERVICE_REQUEST: 'Incorrect service request',
  CANCEL_REASON_VISIT_NO_LONGER_REQUIRED: 'Visit no longer required',
  CANCEL_REASON_FACILITY_ACCESS_ISSUE: 'Facility access issue',
  CANCEL_REASON_OTHER: 'Other',
} as const;

/**
 * UI strings for the technician "Update Task Status" modal (RR-624).
 *
 * @remarks
 * A single-step variant of the admin status-update flow — no "Send email"
 * checkbox and no cancel-reason step. Two strings deliberately differ from
 * {@link UPDATE_SR_STATUS_LABELS}: the title says "zadania" (task) rather than
 * "zgłoszenia" (request), and the optional notes field is labelled "Uwaga"
 * instead of "Notatki administratora" (per the technician design).
 * `NOTES_MAX_LENGTH` (500) matches the backend `admin_notes` cap.
 */
export const TECHNICIAN_UPDATE_SR_STATUS_LABELS = {
  TITLE: 'Zmiana statusu zadania serwisowego',

  // Field labels
  FIELD_CURRENT_STATUS: 'Aktualny status',
  FIELD_NEW_STATUS: 'Nowy status',
  FIELD_NOTES: 'Uwaga',
  FIELD_NOTES_OPTIONAL: '(Opcjonalne)',
  PLACEHOLDER_NOTES: 'Uwaga',

  // Irreversibility banner
  BANNER_IRREVERSIBLE_BOLD: 'Zmiana statusu jest nieodwracalna.',
  BANNER_IRREVERSIBLE_REST:
    ' Po zaktualizowaniu nie będzie można przywrócić poprzedniego statusu.',

  // Helper / empty states
  HELPER_NO_TRANSITIONS: 'Status tego zadania nie może być już zmieniony.',
  EMPTY_NO_OPTIONS: 'Brak dostępnych statusów',

  // Buttons
  BUTTON_CANCEL: 'Anuluj',
  BUTTON_SUBMIT: 'Zmiana statusu',
  BUTTON_SUBMITTING: 'Aktualizowanie statusu…',
  /** Step-2 (Unfulfilled Visit) submit button. */
  BUTTON_SEND: 'Wyślij',

  // Success snackbar
  SUCCESS_MESSAGE: 'Status został pomyślnie zaktualizowany.',

  // Validation
  ERROR_REQUIRED: 'To pole jest wymagane',
  ERROR_NOTES_TOO_LONG: 'Notatka nie może przekraczać 500 znaków.',
  NOTES_MAX_LENGTH: 500,
  PERMISSION_DENIED_SNACKBAR: 'Nie masz uprawnień do wykonania tej czynności',

  // Missed-visit flow (RR-622) — a dedicated second step ("Unfulfilled Visit")
  // shown after the technician selects the `unfulfilled` target on step 1 and
  // clicks "Zmiana statusu". The reason is mandatory; the comment is optional
  // except when the reason is "Other", where it becomes required.
  UNFULFILLED_TITLE: 'Unfulfilled Visit',
  UNFULFILLED_SUBTITLE:
    'Jeśli zaplanowana wizyta się nie odbyła, podaj powód. Pomoże to w informowaniu administratora i klienta.',
  MISSED_VISIT_SECTION: 'Szczegóły zaplanowanej wizyty',
  FIELD_SCHEDULED_DATE: 'Data',
  FIELD_SCHEDULED_TIME: 'Godzina',
  FIELD_SERVICE_TYPE: 'Typ usługi',
  FIELD_CLIENT: 'Nazwa klienta',
  FIELD_LOCATION: 'Lokalizacja',
  FIELD_MISSED_VISIT_REASON: 'Reason for Unfulfilled Visit',
  FIELD_MISSED_VISIT_COMMENT: 'Dodatkowy komentarz',
  FIELD_MISSED_VISIT_COMMENT_OPTIONAL: '(Opcjonalne)',
  PLACEHOLDER_MISSED_VISIT_COMMENT:
    'Enter additional details about the missed visit',
  ERROR_COMMENT_TOO_LONG: 'Komentarz nie może przekraczać 500 znaków.',
} as const;

/** Stable ids for the predefined missed-visit reasons (RR-622). */
export type TechnicianMissedVisitReasonId =
  | 'client_not_available'
  | 'access_denied'
  | 'incorrect_address'
  | 'other';

/**
 * Predefined missed-visit reasons offered to a technician when moving a visit
 * to `unfulfilled`. Labels are kept in English to match the existing admin
 * cancellation-reason set ({@link UPDATE_SR_CANCELLATION_REASONS}).
 */
export const TECHNICIAN_MISSED_VISIT_REASONS: ReadonlyArray<{
  id: TechnicianMissedVisitReasonId;
  label: string;
}> = [
  { id: 'client_not_available', label: 'Client not available' },
  { id: 'access_denied', label: 'Access denied' },
  { id: 'incorrect_address', label: 'Incorrect address' },
  { id: 'other', label: 'Other' },
] as const;

/**
 * UI strings for the cancel service request modal (client only).
 * Covers the illustration title, description, reason textarea, buttons, and success snackbar.
 *
 * @remarks
 * Character limit and the required-field message are shared with the status-update
 * modal — use {@link UPDATE_SR_STATUS_LABELS.NOTES_MAX_LENGTH} and
 * {@link UPDATE_SR_STATUS_LABELS.ERROR_REQUIRED} instead of duplicating them here.
 *
 * Used by `CancelServiceRequestModal` and `useCancelServiceRequest`.
 */
export const CANCEL_SERVICE_REQUEST_LABELS = {
  MODAL_TITLE: 'Anuluj zgłoszenie?',
  DESCRIPTION:
    'Po anulowaniu zgłoszenie serwisowe zostanie wstrzymane, a wizyta technika nie odbędzie się.',
  FIELD_REASON: 'Powód anulowania',
  PLACEHOLDER_REASON: 'Podaj powód anulowania zgłoszenia...',
  BUTTON_BACK: 'Wróć',
  BUTTON_CONFIRM: 'Anuluj zgłoszenie',
  BUTTON_CONFIRMING: 'Anulowanie zgłoszenia…',
  SUCCESS_MESSAGE: 'Zgłoszenie serwisowe zostało pomyślnie anulowane.',
  ERROR_TOO_LONG: 'Powód nie może przekraczać 500 znaków.',
  NO_PRIVILEGE_TOOLTIP: 'Brak uprawnień do anulowania zgłoszenia serwisowego.',
  PERMISSION_DENIED_SNACKBAR:
    'Brak uprawnień do anulowania tego zgłoszenia serwisowego.',
} as const;

export type UpdateSrCancellationReasonId =
  | 'client_unavailable'
  | 'technician_unavailable'
  | 'duplicate_booking'
  | 'incorrect_service_request'
  | 'visit_no_longer_required'
  | 'facility_access_issue'
  | 'other';

export const UPDATE_SR_CANCELLATION_REASONS: ReadonlyArray<{
  id: UpdateSrCancellationReasonId;
  label: string;
}> = [
  {
    id: 'client_unavailable',
    label: UPDATE_SR_STATUS_LABELS.CANCEL_REASON_CLIENT_UNAVAILABLE,
  },
  {
    id: 'technician_unavailable',
    label: UPDATE_SR_STATUS_LABELS.CANCEL_REASON_TECHNICIAN_UNAVAILABLE,
  },
  {
    id: 'duplicate_booking',
    label: UPDATE_SR_STATUS_LABELS.CANCEL_REASON_DUPLICATE_BOOKING,
  },
  {
    id: 'incorrect_service_request',
    label: UPDATE_SR_STATUS_LABELS.CANCEL_REASON_INCORRECT_SERVICE_REQUEST,
  },
  {
    id: 'visit_no_longer_required',
    label: UPDATE_SR_STATUS_LABELS.CANCEL_REASON_VISIT_NO_LONGER_REQUIRED,
  },
  {
    id: 'facility_access_issue',
    label: UPDATE_SR_STATUS_LABELS.CANCEL_REASON_FACILITY_ACCESS_ISSUE,
  },
  {
    id: 'other',
    label: UPDATE_SR_STATUS_LABELS.CANCEL_REASON_OTHER,
  },
] as const;

export const RESCHEDULE_SR_LABELS = {
  // Modal titles
  TITLE: 'Zmień termin zgłoszenia serwisowego',
  CONFIRM_TITLE: 'Review Reschedule',
  CONFIRM_SUBTITLE:
    'Please review the updated schedule before confirming. This will notify relevant users.',
  CONFIRM_NOTIFY:
    'Notify the client & technician that an email containing the relevant details will be sent to them.',

  // Section headings
  SECTION_CURRENT: 'Szczegóły zaplanowanej wizyty',
  SECTION_NEW: 'Nowy termin',
  SECTION_ASSIGNEE: 'Dane osoby przydzielonej',
  SECTION_NOTES: 'Notatki administratora',
  SECTION_PREVIOUS: 'Poprzednio',
  SECTION_AFTER: 'Po zmianie',
  CONFIRM_SECTION_CURRENT: 'Current Schedule Details',
  CONFIRM_SECTION_UPDATED: 'Updated Schedule Details',

  // Assignee picker (only Technician is supported by /reschedule for now,
  // but the UI surfaces a role field for layout consistency with the design).
  FIELD_ASSIGN_TO: 'Przydziel do',
  ASSIGN_TO_TECHNICIAN: 'Technik',
  ASSIGN_TO_ADMIN: 'Admin',

  // Current-visit field labels
  FIELD_START_DATE: 'Data początkowa',
  FIELD_END_DATE: 'Data końcowa',
  FIELD_START_TIME: 'Czas rozpoczęcia',
  FIELD_END_TIME: 'Czas zakończenia',
  FIELD_TIME_OPTIONAL: '(Opcjonalne)',
  FIELD_TASK_FORM_ID: 'Task Form ID',
  FIELD_TASK_FORM_NAME: 'Task Form Name',
  FIELD_CERTIFICATE_ID: 'ID certyfikatu',
  FIELD_CERTIFICATE_TEMPLATE_NAME: 'Certificate Template Name',
  FIELD_SERVICE_TYPE: 'Typ usługi',
  FIELD_ASSIGNED_USER: 'Przydzielony użytkownik',
  FIELD_CLIENT_NAME: 'Nazwa klienta',

  // Technician picker
  FIELD_TECHNICIAN: 'Imię i nazwisko technika',
  FIELD_TECHNICIAN_ID: 'ID technika',
  FIELD_TECHNICIAN_UNCHANGED: 'Technik bez zmian',
  SEARCH_PLACEHOLDER: 'Szukaj po imieniu lub nazwisku',
  TECHNICIAN_HELPER_PREFILL:
    'Aktualnie przydzielony technik: {name}. Potwierdź wybór z listy.',
  TECHNICIAN_TOOLTIP_NO_WINDOW:
    'Please select a start date and end date first.',
  TECHNICIAN_LOADING: 'Ładowanie…',
  TECHNICIAN_NO_RESULTS: 'Nie znaleziono wyników.',

  // Admin notes + notify
  FIELD_ADMIN_NOTES: 'Notatki administratora',
  FIELD_ADMIN_NOTES_OPTIONAL: '(Opcjonalne)',
  PLACEHOLDER_ADMIN_NOTES: 'Notatki administratora',
  FIELD_NOTIFY: 'Send email',
  NOTES_MAX_LENGTH: 500,

  // Buttons
  BUTTON_CANCEL: 'Anuluj',
  BUTTON_REVIEW: 'Przejrzyj zmiany',
  BUTTON_BACK: 'Wstecz',
  BUTTON_SUBMIT: 'Confirm Reschedule',
  BUTTON_SUBMITTING: 'Zapisywanie…',

  // Success snackbars
  SUCCESS_MESSAGE: 'Service request rescheduled successfully.',
  SUCCESS_CLIENT_EMAIL_FAILED:
    'Nie udało się wysłać powiadomienia e-mail do klienta.',
  SUCCESS_TECH_EMAIL_FAILED:
    'Nie udało się wysłać powiadomienia e-mail do technika.',
  SUCCESS_BOTH_EMAILS_FAILED:
    'Nie udało się wysłać powiadomień e-mail do klienta i technika.',

  // Validation / errors
  ERROR_REQUIRED: 'To pole jest wymagane',
  ERROR_START_DATE_AFTER_END:
    'Data początkowa nie może być późniejsza niż data końcowa.',
  ERROR_START_TIME_AFTER_END:
    'Czas rozpoczęcia nie może być późniejszy niż czas zakończenia.',
  ERROR_MIN_DURATION:
    'Minimalny czas trwania musi wynosić co najmniej 30 minut.',
  ERROR_NOTES_TOO_LONG: 'Notatka nie może przekraczać 500 znaków.',
  ERROR_24H_RULE:
    'Zmiana terminu nie jest dozwolona w ciągu 24 godzin przed wizytą.',
  ERROR_NOT_RESCHEDULABLE:
    'Tego zgłoszenia nie można już zmienić w obecnym statusie.',

  // Time picker dialog (reused copy from assign)
  TIME_DIALOG_TITLE: 'Wprowadź godzinę',
  TIME_DIALOG_HOUR: 'Godzina',
  TIME_DIALOG_MINUTE: 'Minuta',
  TIME_DIALOG_OK: 'OK',
} as const;

/**
 * UI strings for the technician accept/reject acknowledgement flow.
 * Covers the accept confirmation dialog, reject dialog (with reason textarea),
 * loading states, success snackbars, and the permission-denied error snackbar.
 * Used by `AcknowledgeServiceRequestModal` and `ViewServiceRequest`.
 */
export const ACKNOWLEDGE_SR_LABELS = {
  // Accept dialog
  ACCEPT_TITLE: 'Zaakceptować zadanie serwisowe?',
  ACCEPT_BODY_PREFIX: 'Czy chcesz zaakceptować to zadanie zaplanowane na ',
  ACCEPT_BODY_TO: ' to ',
  ACCEPT_BUTTON_CONFIRM: 'Zaakceptuj zadanie',
  ACCEPT_BUTTON_SUBMITTING: 'Akceptowanie zadania…',

  // Reject dialog
  REJECT_TITLE: 'Odrzucić zadanie serwisowe?',
  REJECT_BODY_PREFIX: 'Czy chcesz odrzucić to zadanie zaplanowane na ',
  REJECT_HELPER_TEXT:
    'Podaj powód, aby administrator mógł odpowiednio przydzielić to zadanie ponownie.',
  FIELD_REASON: 'Powód odrzucenia',
  REJECT_BUTTON_CONFIRM: 'Odrzuć zadanie',
  REJECT_BUTTON_SUBMITTING: 'Odrzucanie zadania…',

  // Shared
  BUTTON_CANCEL: 'Anuluj',
  REASON_MAX_LENGTH: 500,
  /** Reuses ACKNOWLEDGE_SR_ERROR_MESSAGES.REASON_REQUIRED — single source of truth */
  ERROR_REASON_REQUIRED: ACKNOWLEDGE_SR_ERROR_MESSAGES.REASON_REQUIRED,

  // Snackbars
  ACCEPT_SUCCESS: 'Zadanie zostało pomyślnie zaakceptowane.',
  REJECT_SUCCESS: 'Zadanie zostało pomyślnie odrzucone.',
  /** Reuses ACKNOWLEDGE_SR_ERROR_MESSAGES.PERMISSION_DENIED — single source of truth */
  PERMISSION_DENIED_SNACKBAR: ACKNOWLEDGE_SR_ERROR_MESSAGES.PERMISSION_DENIED,
  /** Reuses ACKNOWLEDGE_SR_ERROR_MESSAGES.ACTION_WINDOW_EXPIRED — single source of truth */
  WINDOW_EXPIRED_SNACKBAR: ACKNOWLEDGE_SR_ERROR_MESSAGES.ACTION_WINDOW_EXPIRED,
} as const;
